<template>
  <div class="bmc">
    <!-- Sammanfattning: senaste vikt + sammansättning omräknad till kg -->
    <div class="bmc-summary">
      <div class="bmc-stat">
        <span class="bmc-stat-label">Vikt</span>
        <span class="bmc-stat-value">{{ fmt(latest.weightKg) }} kg</span>
        <span v-if="weightDelta !== null" class="bmc-delta" :class="weightDelta <= 0 ? 'down' : 'up'">
          {{ weightDelta > 0 ? '+' : '' }}{{ fmt(weightDelta) }} kg
        </span>
      </div>
      <div v-for="c in compParts" :key="c.key" class="bmc-stat" v-show="latest[c.key] != null">
        <span class="bmc-stat-label" :style="{ color: c.color }">{{ c.label }}</span>
        <span class="bmc-stat-value">{{ fmt(latest[c.key]) }}%</span>
        <span class="bmc-stat-kg">≈ {{ fmt(kgOf(latest, c.key)) }} kg</span>
      </div>
    </div>

    <div v-if="points.length < 2" class="bmc-empty">
      Logga minst två mätningar för att se utvecklingen.
    </div>

    <template v-else>
      <h4 class="bmc-h">Viktutveckling</h4>
      <div class="bmc-chart"><canvas ref="weightCanvas"></canvas></div>

      <div v-if="hasComposition" class="bmc-comp-head">
        <h4 class="bmc-h">Sammansättning</h4>
        <div class="bmc-toggle">
          <button :class="{ active: compMode === 'pct' }" @click="compMode = 'pct'">%</button>
          <button :class="{ active: compMode === 'kg' }" @click="compMode = 'kg'">kg</button>
        </div>
      </div>
      <div v-if="hasComposition" class="bmc-chart"><canvas ref="compCanvas"></canvas></div>
    </template>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import {
  Chart, LineController, LineElement, PointElement,
  LinearScale, CategoryScale, Filler, Tooltip, Legend,
} from 'chart.js';

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler, Tooltip, Legend);

export default {
  name: 'BodyMeasurementChart',
  props: {
    measurements: { type: Array, default: () => [] },
  },
  setup(props) {
    const weightCanvas = ref(null);
    const compCanvas = ref(null);
    const compMode = ref('pct');
    let charts = {};

    const compParts = [
      { key: 'musclePercent', label: 'Muskler', color: '#10B981' },
      { key: 'fatPercent', label: 'Fett', color: '#F59E0B' },
      { key: 'waterPercent', label: 'Vatten', color: '#3B82F6' },
    ];

    // Äldst först för grafernas x-axel
    const points = computed(() =>
      [...props.measurements]
        .filter(m => m.weightKg != null)
        .sort((a, b) => (a.date || '').localeCompare(b.date || '')));

    const latest = computed(() => points.value[points.value.length - 1] || {});

    const weightDelta = computed(() => {
      if (points.value.length < 2) return null;
      return points.value[points.value.length - 1].weightKg - points.value[0].weightKg;
    });

    const hasComposition = computed(() =>
      points.value.some(p => p.musclePercent != null || p.fatPercent != null || p.waterPercent != null));

    const fmt = v => (v == null ? '–' : (Math.round(v * 10) / 10).toString().replace('.', ','));
    const kgOf = (m, key) => (m[key] == null || m.weightKg == null ? null : m.weightKg * m[key] / 100);
    const labels = () => points.value.map(p => (p.date || '').slice(0, 10));

    function readAccent() {
      const s = getComputedStyle(document.documentElement);
      return {
        accent: (s.getPropertyValue('--gym-accent') || '#3B82F6').trim(),
        muted: (s.getPropertyValue('--gym-text-dim') || '#888').trim(),
        border: (s.getPropertyValue('--gym-border') || '#333').trim(),
      };
    }

    function destroy() {
      Object.values(charts).forEach(c => { try { c?.destroy(); } catch { /* */ } });
      charts = {};
    }

    async function render() {
      await nextTick();
      destroy();
      if (points.value.length < 2) return;
      const tc = readAccent();
      const lbl = labels();

      if (weightCanvas.value) {
        charts.weight = new Chart(weightCanvas.value, {
          type: 'line',
          data: {
            labels: lbl,
            datasets: [{
              label: 'Vikt', data: points.value.map(p => p.weightKg),
              borderColor: tc.accent, backgroundColor: tc.accent + '22',
              borderWidth: 2, fill: true, tension: 0.35,
              pointRadius: points.value.length > 30 ? 0 : 3, pointBackgroundColor: tc.accent,
            }],
          },
          options: baseOptions(tc, ' kg'),
        });
      }

      if (compCanvas.value && hasComposition.value) {
        const kg = compMode.value === 'kg';
        const datasets = compParts
          .filter(c => points.value.some(p => p[c.key] != null))
          .map(c => ({
            label: c.label,
            data: points.value.map(p => (p[c.key] == null ? null : (kg ? kgOf(p, c.key) : p[c.key]))),
            borderColor: c.color, backgroundColor: c.color + '22',
            borderWidth: 2, fill: false, tension: 0.35, spanGaps: true,
            pointRadius: points.value.length > 30 ? 0 : 3, pointBackgroundColor: c.color,
          }));
        charts.comp = new Chart(compCanvas.value, {
          type: 'line',
          data: { labels: lbl, datasets },
          options: baseOptions(tc, kg ? ' kg' : '%', true),
        });
      }
    }

    function baseOptions(tc, unit, legend = false) {
      return {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: legend, labels: { color: tc.muted, font: { size: 11 }, boxWidth: 12 } },
          tooltip: { callbacks: { label: ctx => ` ${ctx.dataset.label}: ${Math.round(ctx.parsed.y * 10) / 10}${unit}` } },
        },
        scales: {
          x: { grid: { display: false }, ticks: { color: tc.muted, font: { size: 10 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 8 } },
          y: { grid: { color: tc.border }, ticks: { color: tc.muted, font: { size: 10 } } },
        },
      };
    }

    watch(() => props.measurements, render, { deep: true });
    watch(compMode, render);
    onMounted(render);
    onBeforeUnmount(destroy);

    return {
      weightCanvas, compCanvas, compMode, compParts,
      points, latest, weightDelta, hasComposition, fmt, kgOf,
    };
  },
};
</script>

<style scoped>
.bmc { display: flex; flex-direction: column; gap: 0.75rem; }
.bmc-summary { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.bmc-stat {
  flex: 1 1 90px; background: var(--gym-surface-2); border: 1px solid var(--gym-border);
  border-radius: var(--gym-radius-sm); padding: 0.6rem 0.7rem; display: flex; flex-direction: column; gap: 0.15rem;
}
.bmc-stat-label { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--gym-text-dim); }
.bmc-stat-value { font-size: 1.15rem; font-weight: 700; color: var(--gym-text); }
.bmc-stat-kg { font-size: 0.75rem; color: var(--gym-text-dim); }
.bmc-delta { font-size: 0.75rem; font-weight: 600; }
.bmc-delta.down { color: #10B981; }
.bmc-delta.up { color: #F59E0B; }
.bmc-h { margin: 0.3rem 0 0; font-size: 0.9rem; color: var(--gym-text); }
.bmc-chart { height: 180px; }
.bmc-empty { color: var(--gym-text-dim); font-size: 0.9rem; padding: 0.5rem 0; }
.bmc-comp-head { display: flex; align-items: center; justify-content: space-between; }
.bmc-toggle { display: flex; gap: 0.25rem; }
.bmc-toggle button {
  padding: 0.25rem 0.7rem; border: 1px solid var(--gym-border); background: var(--gym-surface-2);
  color: var(--gym-text-dim); border-radius: var(--gym-radius-sm); cursor: pointer; font-size: 0.85rem;
}
.bmc-toggle button.active { background: var(--gym-accent); color: #fff; border-color: var(--gym-accent); }
</style>
